import { ACCEPTED_FILE_TYPES_DEFAULT, ACCEPT_ATTRIBUTE_DEFAULT, MAX_FILE_SIZE } from "@/constants";
import { getFileSize } from "@/utils/getFileSize";
import { getFormattedFileTypeErrorMessage } from "@/utils/validationMessages";
import Image from "next/image";
import React, { useEffect, useState } from "react";
import { UseFormTrigger } from "react-hook-form";
import { FiArrowUpCircle } from "react-icons/fi";
import { GrDocumentText } from "react-icons/gr";

const FileUploadField = React.forwardRef<
	HTMLInputElement,
	{
		errorMessage?: string;
		label: string;
		registerName: string;
		// todo add type
		setValue: (x: any, y: any) => void;
		setError: (x: any, y: any) => void;
		trigger?: UseFormTrigger<any>;
		isFileNull?: boolean;
		uniqueId?: string;
		className?: string;
		acceptAttribute?: string;
		acceptedTypes?: string[];
		customOnChange?: (file: File | null) => void;
	}
>(
	(
		{
			errorMessage,
			label,
			setValue,
			setError,
			registerName,
			uniqueId,
			className,
			acceptAttribute = ACCEPT_ATTRIBUTE_DEFAULT,
			acceptedTypes = ACCEPTED_FILE_TYPES_DEFAULT,
			trigger,
			customOnChange,
			...otherProps
		},
		ref,
	) => {
		const [isFileHoverOnDrop, setFileHoverOnDrop] = useState(false);
		const [file, setFile] = useState<File | null>(null);

		const [fileError, setFileError] = useState("");

		const validateFile = (fileToValidate: File): { isValid: boolean; error?: string } => {
			if (fileToValidate.size > MAX_FILE_SIZE) {
				return { isValid: false, error: `File is over 10MB` };
			}
			if (!acceptedTypes.includes(fileToValidate?.type)) {
				return { isValid: false, error: getFormattedFileTypeErrorMessage(acceptAttribute) };
			}
			return { isValid: true };
		};

		const handleFileChange = (fileToHandle: File | null, inputElement?: HTMLInputElement) => {
			if (!fileToHandle) {
				setFile(null);
				if (customOnChange) {
					customOnChange(null);
				}
				return;
			}

			const validation = validateFile(fileToHandle);
			if (!validation.isValid) {
				setFile(fileToHandle);
				setFileError(validation.error || "");
				if (inputElement) {
					inputElement.value = "";
				}
				return;
			}

			// Validation passed
			setFile(fileToHandle);
			setFileError("");

			if (customOnChange) {
				customOnChange(fileToHandle);
			} else {
				setValue(registerName, fileToHandle);
				setError(registerName, "");
				trigger?.(registerName);
			}
		};

		useEffect(() => {
			if (!otherProps.isFileNull) {
				setFile(null);
			}
		}, [otherProps.isFileNull]);

		return (
			<div>
				<input
					ref={ref}
					id={uniqueId || "browse-file-button"}
					type="file"
					className="hidden"
					accept={acceptAttribute ?? ACCEPT_ATTRIBUTE_DEFAULT}
					{...otherProps}
					// onChange={(e) => {
					// 	if (e.target.files && e.target.files.length > 0) {
					// 		setFile(e.target.files[0]);
					// 		setValue(registerName, e.target.files[0]);
					// 		setError(registerName, "");
					// 	} else {
					// 		setFile(null);
					// 	}
					// }}
					onChange={(e) => {
						if (e.target.files && e.target.files.length > 0) {
							handleFileChange(e.target.files[0], e.target);
						} else {
							handleFileChange(null);
						}
					}}
					hidden
				/>
				{label && <label className="my-2  text-sm font-bold">{label}</label>}

				{fileError && (
					<div className="w-full flex-col justify-end">
						<div className="mt-12 w-full justify-center border border-clearSky-500 p-12">
							<div className="relative flex w-full items-center justify-between">
								<div className="flex w-[90%] gap-4">
									<div className="flex h-fit items-center justify-center rounded-full bg-clearSky-500 p-2">
										<GrDocumentText size="20px" />
									</div>
									<div className="">
										<p>{file?.name}</p>
										<p className="mt-1 text-sm text-red">{fileError}</p>
									</div>
								</div>
								{/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
								<div
									onClick={() => {
										setFileError("");
										setFile(null);
									}}
								>
									<Image
										src="/refresh.svg"
										height={24}
										width={24}
										alt="co2_retirement_icon"
										className="absolute right-0 top-0 h-6 w-6 cursor-pointer"
									/>
								</div>
							</div>
						</div>
					</div>
				)}

				{!fileError && (
					<label
						htmlFor={uniqueId || `browse-file-button`}
						aria-label={label}
						className={`group mt-2 flex h-64 cursor-pointer items-center justify-center rounded-2xl border-dashed bg-white transition-all hover:animate-pulse hover:border hover:border-dashed hover:border-clearSky-500 hover:bg-sky-50 ${className} ${
							isFileHoverOnDrop ? "animate-pulse border border-secondary" : ""
						} ${errorMessage ? "border border-red-500" : "hover:border-dashed hover:border-secondary"}`}
					>
						<div
							onDragEnter={() => {
								setFileHoverOnDrop(true);
							}}
							onDragLeave={() => {
								setFileHoverOnDrop(false);
							}}
							onDragOver={(e) => {
								e.preventDefault();
								setFileHoverOnDrop(true);
							}}
							onDrop={(e) => {
								e.preventDefault();
								setFileHoverOnDrop(false);
								if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
									handleFileChange(e.dataTransfer.files[0]);
								} else {
									handleFileChange(null);
								}
							}}
						>
							<div className="flex flex-col items-center justify-center space-y-4 text-lg text-gray-600 ">
								<div className="rounded-full bg-clearSky-500 p-2">
									<FiArrowUpCircle />
								</div>
								<div>{file && <span>{decodeURIComponent(file.name)}</span>}</div>

								<span className="max-w-sm text-center group-hover:hidden group-hover:opacity-0">
									Drag and drop or click here to upload PDF, DOCX (max {getFileSize(MAX_FILE_SIZE)})
								</span>
								<span className="hidden max-w-sm text-center text-gray-600 opacity-0 group-hover:block group-hover:opacity-100">
									Drop file here
								</span>
							</div>
						</div>
					</label>
				)}

				{!fileError && errorMessage && <p className="my-2 text-xs font-medium text-red-500">{errorMessage}</p>}

				{/*{file && (*/}
				{/*	<Button*/}
				{/*		variant="outline"*/}
				{/*		className="mb-6 mr-4 mt-4 flex items-center space-x-4 whitespace-nowrap"*/}
				{/*		color="secondary"*/}
				{/*		onClick={() => {*/}
				{/*			setFile(null);*/}
				{/*			setValue(registerName, "");*/}
				{/*		}}*/}
				{/*	>*/}
				{/*		<>*/}
				{/*			{decodeURIComponent(file.name)} {getFileSize(file.size)} <RxCross1 className="ml-4" />*/}
				{/*		</>*/}
				{/*	</Button>*/}
				{/*)}*/}
			</div>
		);
	},
);

FileUploadField.displayName = "FileUploadField";
export default FileUploadField;
